import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SupplierProvider } from "@/context/SupplierContext";
import BuyerSwitcher from "@/components/BuyerSwitcher";
import { LayoutDashboard, FileText, AlertCircle, User, LogOut, MessageSquare, FolderOpen, Inbox, MessagesSquare, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/seller/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/seller/pos", label: "My Purchase Orders", icon: FileText },
  { to: "/seller/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/seller/updates", label: "Updates Required", icon: AlertCircle },
  { to: "/seller/communication", label: "Communication", icon: MessageSquare },
  { to: "/seller/chat", label: "Chat", icon: MessagesSquare },
  { to: "/seller/support", label: "Support", icon: LifeBuoy },
  { to: "/seller/documents", label: "Documents", icon: FolderOpen },
  { to: "/seller/profile", label: "Profile", icon: User },
];

function Shell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [chatUnread, setChatUnread] = useState(0);
  useEffect(() => {
    const f = () => api.get("/chat/unread-count").then((r) => setChatUnread(r.data.count)).catch(() => {});
    f(); const t = setInterval(f, 20000); return () => clearInterval(t);
  }, []);
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b bg-white">
        <div className="mx-auto flex h-16 max-w-5xl items-center gap-5 px-4">
          <div className="flex items-center gap-2">
            <img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-7 w-auto" />
            <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Seller</span>
          </div>
          <BuyerSwitcher />
          <nav className="hidden items-center gap-1 lg:flex" data-testid="supplier-nav">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`supplier-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors duration-150 ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`
                }>
                <n.icon className="h-3.5 w-3.5" />{n.label}
                {n.to === "/seller/chat" && chatUnread > 0 && (
                  <span data-testid="seller-chat-unread-badge" className="rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{chatUnread}</span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <div className="hidden text-right md:block">
              <p className="max-w-40 truncate text-sm font-medium leading-tight" title={user?.supplier?.name || user?.name}>{user?.supplier?.name || user?.name}</p>
              <p className="text-xs text-muted-foreground leading-tight">{user?.name}</p>
            </div>
            <Button variant="outline" size="icon" data-testid="supplier-logout-btn" onClick={async () => { await logout(); navigate("/login"); }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-5xl items-center gap-1 overflow-x-auto px-4 pb-2 lg:hidden" data-testid="supplier-nav-mobile">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium ${isActive ? "bg-slate-900 text-white" : "text-slate-600"}`
              }>
              <n.icon className="h-3.5 w-3.5" />{n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-5xl p-6">
        <Outlet />
      </main>
    </div>
  );
}

export default function SupplierLayout() {
  return (
    <SupplierProvider>
      <Shell />
    </SupplierProvider>
  );
}
